import { useState } from "react";

import { Button, Callout } from "@blueprintjs/core";

import { Box } from "@/components/layout/flexbox";
import { CardAboveXs } from "@/components/pieces/CardAboveXs";
import { LIGHT_STEEL_GRAY } from "@/css/constants";
import { useSaveQuizAnswerMutation } from "@/graphql";
import { FullRoleplaySession } from "@/types/roleplays";
import { ensureNonNullArray } from "@/utils/array";
import { NonNullableArrayItem } from "@/utils/types";

type FormField = NonNullableArrayItem<FullRoleplaySession["formFields"]>;
type FormFieldOption =
  | NonNullable<
      NonNullableArrayItem<FormField["filteredAnswers"]>["optionValue"]
    >
  | NonNullableArrayItem<FormField["formFieldOptions"]>;

const getButtonStyle = (isChosen: boolean, isCorrectOption?: boolean) => {
  if (!isChosen) return {};
  return {
    border: isCorrectOption ? "1px solid #0D8050" : "1px solid #C61D1D",
    color: isCorrectOption ? "#0D8050" : "#C61D1D",
  };
};

interface QuizQuestionProps {
  formField: FormField;
  roleplaySession: FullRoleplaySession;
  allowToAnswer?: boolean;
  isProgramLeadView?: boolean;
}

function QuizQuestion({
  formField,
  roleplaySession,
  allowToAnswer = true,
  isProgramLeadView,
}: QuizQuestionProps) {
  const [errMsg, setErrMsg] = useState<string>();
  const defaultOption = formField.filteredAnswers?.[0]?.optionValue;
  const [isCorrect, setIsCorrect] = useState(defaultOption?.isCorrect);
  const [explanation, setExplanation] = useState(defaultOption?.explanation);
  const [loading, setLoading] = useState(false);

  const [saveAnswer] = useSaveQuizAnswerMutation({
    onCompleted: (data) => {
      if (data.saveQuizAnswer) {
        const { isCorrect, explanation, ok, msg } = data.saveQuizAnswer;
        setIsCorrect(isCorrect);
        setExplanation(explanation);
        if (!ok && msg) {
          setErrMsg(msg);
        }
      }
    },
    onError: () => {
      setErrMsg("Failed to save answer");
    },
  });

  const filteredAnswers = ensureNonNullArray(formField.filteredAnswers);

  const [chosenOption, setChoosenOption] = useState<FormFieldOption | null>(
    filteredAnswers[0]?.optionValue ?? null,
  );

  const handleOptionClick = (option: FormFieldOption) => {
    setLoading(true);
    saveAnswer({
      variables: {
        formFieldId: formField.id,
        optionValueId: option.id,
        roleplaySessionId: roleplaySession.id,
      },
    })
      .then((res) => {
        if (res.data?.saveQuizAnswer?.ok) {
          setChoosenOption(option);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Box p={24} mt={24} bg={LIGHT_STEEL_GRAY} css={{ borderRadius: 8 }}>
      <Box fontSize={16} fontWeight={600}>
        {formField.prompt}
      </Box>
      <Box mt={16}>
        {ensureNonNullArray(formField.formFieldOptions).map((option, index) => {
          const isChosen = chosenOption?.id === option?.id;
          const isCorrectOption = isChosen && isCorrect;

          return (
            <Box key={index} mt={16}>
              <Button
                disabled={!!chosenOption || loading || !allowToAnswer}
                onClick={() => {
                  handleOptionClick(option);
                }}
                style={{
                  minWidth: 400,
                  height: 42,
                  ...getButtonStyle(isChosen, isCorrectOption ?? false),
                }}
              >
                {option?.label}
              </Button>
            </Box>
          );
        })}
        {chosenOption ? (
          <Box
            color={isCorrect ? "#0D8050" : "#C61D1D"}
            mt={24}
            bg="white"
            p={16}
            style={{ borderRadius: 6 }}
          >
            <Box fontWeight={700} fontSize={16}>
              {isCorrect ? "Correct!" : "Not Quite"}
            </Box>
            <Box>{explanation}</Box>
          </Box>
        ) : isProgramLeadView ? (
          <Box p={16} fontStyle="italic">
            * Participant has not answered yet
          </Box>
        ) : (
          <></>
        )}
      </Box>
      {errMsg && (
        <Box mt={2}>
          <Callout intent="danger">{errMsg}</Callout>
        </Box>
      )}
    </Box>
  );
}

interface RoleplaySessionQuizProps {
  roleplaySession: FullRoleplaySession;
  isProgramLeadView: boolean;
}

export function RoleplaySessionQuiz({
  roleplaySession,
  isProgramLeadView,
}: RoleplaySessionQuizProps) {
  const formFields = ensureNonNullArray(roleplaySession.formFields);

  if (formFields?.length === 0) return null;

  const title = !isProgramLeadView
    ? `Please answer the question${formFields?.length > 1 ? "s" : ""} below`
    : "Quiz results from the best performance session";
  return (
    <Box my={16}>
      <CardAboveXs>
        <Box fontSize={20} fontWeight={600} mb={16}>
          {title}
        </Box>
        {formFields.map((formField, index) => {
          const shouldShowQuestion = formField.formFieldOptions?.length;
          if (!shouldShowQuestion) return null;

          return (
            <QuizQuestion
              key={index}
              formField={formField}
              roleplaySession={roleplaySession}
              allowToAnswer={!isProgramLeadView}
              isProgramLeadView={isProgramLeadView}
            />
          );
        })}
      </CardAboveXs>
    </Box>
  );
}
